import { parseLetterAppointmentContext } from "../letter-table-tags"
import type { InboxWorkflowStepPreviewResult } from "@kontextwerk/control-web-frame/inbox-workflow/contract"
import type { ScheduleEventCommunicationPresentation } from "../../../../../../../../../../../api/kehrblatt/work-orders"
export type PrintSelectionAppointmentStatus = {
  value: string
  label: string
  icon?: string
  color?: string
  description?: string
}

export type PrintSelectionPreviewRow = import("../letter-table-tags").LetterAppointmentContext & {
  number: number
  letter_delivery_batch_item_id: string
  action_case_id?: string | null
  contact_order_step_id: string
  work_order_id?: string | null
  print_mode?: string | null
  printable: boolean
  disabled_reason?: string | null
  recommended_print: boolean
  recommendation_reason_code: string
  recommendation_text: string
  recommendation_tag_label: string
  recommendation_tag_color: string
  recommendation_short_reason: string
  order_number?: string | null
  order_type_internal_label?: string | null
  unit_label?: string | null
  order_label: string
  appointment_label: string
  appointment_status?: PrintSelectionAppointmentStatus | null
  communication_presentation?: ScheduleEventCommunicationPresentation | null
  appointment_status_code?: string | null
  appointment_start_at?: string | null
  appointment_end_at?: string | null
  work_order_view_status?: string | null
  appointment_sub_label?: string | null
  appointment_tooltip_content?: string | null
  appointment_fallback_label: string
  recipient_label: string
  property_label: string
  status_label: string
  selection_row?: Record<string, unknown> | null
}

export type PrintSelectionPreviewPayload = {
  rows: PrintSelectionPreviewRow[]
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

const stringValue = (value: unknown): string => String(value ?? "").trim()

const optionalString = (value: unknown): string | null => {
  const text = stringValue(value)

  return text || null
}

const statusFromPayload = (
  value: unknown,
  fallbackLabel: string,
): PrintSelectionAppointmentStatus | null => {
  const status = asRecord(value)
  const label = stringValue(status.label) || fallbackLabel

  if (!label) return null

  return {
    value: stringValue(status.value) || "LETTER_DELIVERY_APPOINTMENT",
    label,
    icon: optionalString(status.icon) ?? undefined,
    color: stringValue(status.color) || "neutral",
    description: optionalString(status.description) ?? undefined,
  }
}

const rowFromPayload = (value: unknown, index: number): PrintSelectionPreviewRow | null => {
  const row = asRecord(value)
  const itemId = stringValue(row.letter_delivery_batch_item_id)
  const contactOrderStepId = stringValue(row.contact_order_step_id)

  if (!itemId || !contactOrderStepId) return null

  const appointmentLabel = stringValue(row.appointment_label) || "Termin aus Terminbrief"
  const printable = row.printable === true
  const recommendedPrint = row.recommended_print === true
  const fallbackRecommendationLabel = printable && !recommendedPrint ? "Nicht nötig" : "Notwendig"
  const fallbackRecommendationColor = printable && !recommendedPrint ? "emerald" : "rose"
  const fallbackShortReason = printable
    ? recommendedPrint
      ? "Noch nicht gedruckt"
      : "Nicht empfohlen"
    : "Nicht druckbar"

  return {
    ...parseLetterAppointmentContext(row),
    number: Number.isFinite(Number(row.number)) ? Number(row.number) : index + 1,
    letter_delivery_batch_item_id: itemId,
    action_case_id: optionalString(row.action_case_id),
    contact_order_step_id: contactOrderStepId,
    work_order_id: optionalString(row.work_order_id),
    print_mode: optionalString(row.print_mode),
    printable,
    disabled_reason: optionalString(row.disabled_reason),
    recommended_print: recommendedPrint,
    recommendation_reason_code: stringValue(row.recommendation_reason_code),
    recommendation_text: stringValue(row.recommendation_text),
    recommendation_tag_label:
      stringValue(row.recommendation_tag_label) || fallbackRecommendationLabel,
    recommendation_tag_color:
      stringValue(row.recommendation_tag_color) || fallbackRecommendationColor,
    recommendation_short_reason:
      stringValue(row.recommendation_short_reason) || fallbackShortReason,
    order_number: optionalString(row.order_number),
    order_type_internal_label: optionalString(row.order_type_internal_label),
    unit_label: optionalString(row.unit_label),
    order_label: stringValue(row.order_label) || "Auftrag",
    appointment_label: appointmentLabel,
    appointment_status: statusFromPayload(row.appointment_status, appointmentLabel),
    communication_presentation:
      row.communication_presentation && typeof row.communication_presentation === "object"
        ? (row.communication_presentation as ScheduleEventCommunicationPresentation)
        : null,
    appointment_status_code: optionalString(row.appointment_status_code),
    appointment_start_at: optionalString(row.appointment_start_at),
    appointment_end_at: optionalString(row.appointment_end_at),
    work_order_view_status: optionalString(row.work_order_view_status),
    appointment_sub_label: optionalString(row.appointment_sub_label),
    appointment_tooltip_content: optionalString(row.appointment_tooltip_content),
    appointment_fallback_label: stringValue(row.appointment_fallback_label) || appointmentLabel,
    recipient_label: stringValue(row.recipient_label) || "Empfänger aus Terminbrief",
    property_label: stringValue(row.property_label) || "Liegenschaft aus Terminbrief",
    status_label: stringValue(row.status_label) || "Offen",
    selection_row:
      row.selection_row && typeof row.selection_row === "object"
        ? (row.selection_row as Record<string, unknown>)
        : null,
  }
}

export const parsePrintSelectionPreviewPayload = (
  payload: unknown,
): PrintSelectionPreviewPayload => {
  const data = asRecord(payload)
  const rows = Array.isArray(data.rows)
    ? data.rows.map(rowFromPayload).filter((row): row is PrintSelectionPreviewRow => row !== null)
    : []

  return { rows }
}

export const parsePrintSelectionPreviewResult = (
  result: InboxWorkflowStepPreviewResult,
): PrintSelectionPreviewPayload => {
  if (result.flow_key !== "letter_delivery_batch") {
    throw new Error("Unerwarteter Flow im Druckauswahl-Preview.")
  }

  if (result.step_key !== "deliver_appointment_letters") {
    throw new Error("Unerwarteter Step im Druckauswahl-Preview.")
  }

  if (result.preview_kind !== PRINT_SELECTION_PREVIEW_KIND) {
    throw new Error("Unerwarteter Typ des Druckauswahl-Previews.")
  }

  return parsePrintSelectionPreviewPayload(result.payload)
}

export const PRINT_SELECTION_PREVIEW_KIND = "letter_delivery_print_selection"
